// ---------------------------------------------------------------------------
// AG-019 Phase 3 — Persona language pack persistence table
// ---------------------------------------------------------------------------
//
// Per-tenant `(persona, role) → terminology` overrides. Backs the Phase 3
// agency route `GET/PUT/DELETE /agency/personas/language-pack` and the
// frontend `usePersonaPack()` hook.
//
// One row = one pack. The unique constraint on `(tenant_id, persona, role)`
// is what lets the Phase 1 resolver fold rows into the 4-layer fallback
// chain `(persona, role) → (persona, "*") → ("*", role) → DEFAULT_TERMINOLOGY`
// without de-duping at read time.
//
// Wildcards:
//  - `persona = "*"` rows act as the cross-persona role-wide override layer.
//  - `role    = "*"` rows act as the persona-wide override layer.
//
// CQRS hooks:
//  - Upsert  -> `tenant.persona_language_pack.set@v1`
//  - Delete  -> `tenant.persona_language_pack.cleared@v1`
//
// Tenant isolation: every query MUST filter by `tenant_id` first. The agency
// route enforces this at the handler level (no RLS — application-level auth
// per CLAUDE.md §"Tenant Isolation").
// ---------------------------------------------------------------------------

import { pgTable, text, timestamp, jsonb, uniqueIndex, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/**
 * Persona axes that a pack row may target. Mirrors the Phase 1 `PERSONA_AXES`
 * + the persona-axis wildcard `*` used for cross-persona role-wide packs.
 *
 * Stored as text rather than a pg enum because the wildcard `*` and future
 * persona axes (e.g. `partner`) must be additive without a destructive
 * `ALTER TYPE` migration.
 */
export const TENANT_PERSONA_LANGUAGE_PACK_AXES = [
  "owner",
  "agency",
  "staff",
  "client",
  "*",
] as const;

export type TenantPersonaLanguagePackAxis =
  (typeof TENANT_PERSONA_LANGUAGE_PACK_AXES)[number];

export const tenantPersonaLanguagePacks = pgTable(
  "tenant_persona_language_packs",
  {
    // Surrogate id so callers can target a row without juggling the
    // composite key tuple.
    id: text("id").primaryKey(),
    tenantId: text("tenant_id").notNull(),
    persona: text("persona").notNull(),
    role: text("role").notNull(),
    /**
     * Map of canonical terminology slots (`deal`, `contact`, etc.) to the
     * human label for this (persona, role). Stored as JSONB so the resolver
     * can pull all packs for a tenant in one query and project the merged
     * map application-side.
     */
    terminology: jsonb("terminology").notNull().$type<Record<string, string>>(),
    actorUserId: text("actor_user_id").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Resolver hot path: pull every pack for one tenant in one round trip.
    index("idx_tenant_persona_language_packs_tenant").on(table.tenantId),
    // The (persona, role) tuple within a tenant is the natural primary key
    // of the fallback chain. Enforce uniqueness so the resolver never sees
    // ambiguous overrides.
    uniqueIndex("uq_tenant_persona_language_packs_tenant_persona_role").on(
      table.tenantId,
      table.persona,
      table.role,
    ),
  ],
);

export type TenantPersonaLanguagePack =
  typeof tenantPersonaLanguagePacks.$inferSelect;
export type NewTenantPersonaLanguagePack =
  typeof tenantPersonaLanguagePacks.$inferInsert;
